/**
 * Kokoro-82M TTS via kokoro-js (Transformers.js under the hood).
 * 29 English preset voices, native speed, Apache-2.0.
 */

import { loadKokoroJs } from './cdn';
import { LoadProgress, PresetVoice, SynthOptions, SynthResult, TtsEngine } from './types';
import { concatPcm, splitSentences } from '../audioUtils';

const KOKORO_VOICES: PresetVoice[] = [
  { id: 'af_heart', label: 'Heart (US female)', lang: 'en', gender: 'female' },
  { id: 'af_alloy', label: 'Alloy (US female)', lang: 'en', gender: 'female' },
  { id: 'af_aoede', label: 'Aoede (US female)', lang: 'en', gender: 'female' },
  { id: 'af_bella', label: 'Bella (US female)', lang: 'en', gender: 'female' },
  { id: 'af_jessica', label: 'Jessica (US female)', lang: 'en', gender: 'female' },
  { id: 'af_kore', label: 'Kore (US female)', lang: 'en', gender: 'female' },
  { id: 'af_nicole', label: 'Nicole (US female)', lang: 'en', gender: 'female' },
  { id: 'af_nova', label: 'Nova (US female)', lang: 'en', gender: 'female' },
  { id: 'af_river', label: 'River (US female)', lang: 'en', gender: 'female' },
  { id: 'af_sarah', label: 'Sarah (US female)', lang: 'en', gender: 'female' },
  { id: 'af_sky', label: 'Sky (US female)', lang: 'en', gender: 'female' },
  { id: 'am_adam', label: 'Adam (US male)', lang: 'en', gender: 'male' },
  { id: 'am_echo', label: 'Echo (US male)', lang: 'en', gender: 'male' },
  { id: 'am_eric', label: 'Eric (US male)', lang: 'en', gender: 'male' },
  { id: 'am_fenrir', label: 'Fenrir (US male)', lang: 'en', gender: 'male' },
  { id: 'am_liam', label: 'Liam (US male)', lang: 'en', gender: 'male' },
  { id: 'am_michael', label: 'Michael (US male)', lang: 'en', gender: 'male' },
  { id: 'am_onyx', label: 'Onyx (US male)', lang: 'en', gender: 'male' },
  { id: 'am_puck', label: 'Puck (US male)', lang: 'en', gender: 'male' },
  { id: 'am_santa', label: 'Santa (US male)', lang: 'en', gender: 'male' },
  { id: 'bf_alice', label: 'Alice (GB female)', lang: 'en', gender: 'female' },
  { id: 'bf_emma', label: 'Emma (GB female)', lang: 'en', gender: 'female' },
  { id: 'bf_isabella', label: 'Isabella (GB female)', lang: 'en', gender: 'female' },
  { id: 'bf_lily', label: 'Lily (GB female)', lang: 'en', gender: 'female' },
  { id: 'bm_daniel', label: 'Daniel (GB male)', lang: 'en', gender: 'male' },
  { id: 'bm_fable', label: 'Fable (GB male)', lang: 'en', gender: 'male' },
  { id: 'bm_george', label: 'George (GB male)', lang: 'en', gender: 'male' },
  { id: 'bm_lewis', label: 'Lewis (GB male)', lang: 'en', gender: 'male' },
];

const REPO = 'onnx-community/Kokoro-82M-v1.0-ONNX';
let ttsInstance: any = null;
let loadPromise: Promise<any> | null = null;
let abortFlag = false;

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

async function detectDevice(): Promise<string> {
  const win = getWin();
  try {
    if (win.navigator && win.navigator.gpu) {
      const adapter = await win.navigator.gpu.requestAdapter();
      if (adapter) return 'webgpu';
    }
  } catch {
    /* ignore */
  }
  return 'wasm';
}

async function ensureKokoro(onProgress?: (info: LoadProgress) => void): Promise<any> {
  if (ttsInstance) return ttsInstance;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    onProgress && onProgress({ status: 'Đang tải thư viện Kokoro…', progress: 0.05 });
    const mod = await loadKokoroJs();
    const KokoroTTS = mod.KokoroTTS || (mod.default && mod.default.KokoroTTS) || mod.default;
    if (!KokoroTTS || !KokoroTTS.from_pretrained) {
      throw new Error('kokoro-js không khởi tạo được (thiếu KokoroTTS).');
    }
    const device = await detectDevice();
    onProgress &&
      onProgress({
        status: 'Đang tải model Kokoro (~86MB, lần đầu)… thiết bị: ' + device,
        progress: 0.1,
      });

    const tts = await KokoroTTS.from_pretrained(REPO, {
      dtype: 'q8',
      device,
      progress_callback: (data: any) => {
        if (!onProgress) return;
        let progress: number | undefined;
        if (typeof data.progress === 'number') {
          const raw = data.progress > 1 ? data.progress / 100 : data.progress;
          progress = Math.max(0, Math.min(1, raw));
        } else if (data.loaded != null && data.total) {
          progress = Math.max(0, Math.min(1, data.loaded / data.total));
        }
        onProgress({
          status: 'Đang tải model… ' + ((data && data.status) || ''),
          progress,
          file: data && data.file,
        });
      },
    });
    ttsInstance = tts;
    onProgress && onProgress({ status: 'Model sẵn sàng.', progress: 1 });
    return tts;
  })();

  try {
    return await loadPromise;
  } catch (e) {
    loadPromise = null;
    throw e;
  }
}

function extractPcm(audio: any): { pcm: Float32Array; sampleRate: number } {
  // kokoro-js returns RawAudio-like { audio: Float32Array, sampling_rate } or Audio object
  if (!audio) throw new Error('Kokoro không trả về âm thanh.');
  if (audio.audio instanceof Float32Array) {
    return {
      pcm: audio.audio,
      sampleRate: audio.sampling_rate || audio.sample_rate || 24000,
    };
  }
  if (audio instanceof Float32Array) {
    return { pcm: audio, sampleRate: 24000 };
  }
  if (audio.data instanceof Float32Array) {
    return { pcm: audio.data, sampleRate: audio.sampleRate || 24000 };
  }
  // TypedArray-like
  if (audio.audio && audio.audio.length != null) {
    return {
      pcm: new Float32Array(audio.audio),
      sampleRate: audio.sampling_rate || 24000,
    };
  }
  throw new Error('Định dạng âm thanh Kokoro không nhận diện được.');
}

export const kokoroEngine: TtsEngine = {
  id: 'kokoro',
  supportsCloning: false,
  supportsNativeSpeed: true,
  supportsExaggeration: false,
  canExportFile: true,

  ensureLoaded: async (_modelId: string, onProgress?: (info: LoadProgress) => void) => {
    await ensureKokoro(onProgress);
  },

  isReady: () => !!ttsInstance,

  listVoices: async () => KOKORO_VOICES.slice(),

  synthesize: async (_modelId: string, opts: SynthOptions): Promise<SynthResult> => {
    abortFlag = false;
    const tts = await ensureKokoro(opts.onProgress);
    const text = (opts.text || '').trim();
    if (!text) throw new Error('Hãy nhập văn bản.');
    const voice = opts.voiceId || 'af_heart';
    const speed = typeof opts.speed === 'number' && opts.speed > 0 ? opts.speed : 1;
    const sentences = splitSentences(text);
    const chunks: Float32Array[] = [];
    let sampleRate = 24000;

    for (let i = 0; i < sentences.length; i++) {
      if (abortFlag || (opts.signal && opts.signal.aborted)) {
        throw new Error('Đã hủy.');
      }
      opts.onProgress &&
        opts.onProgress({
          status: 'Đang tổng hợp ' + (i + 1) + '/' + sentences.length + '…',
          progress: (i + 1) / sentences.length,
        });
      const audio = await tts.generate(sentences[i], { voice, speed });
      const extracted = extractPcm(audio);
      sampleRate = extracted.sampleRate;
      chunks.push(extracted.pcm);
      chunks.push(new Float32Array(Math.floor(sampleRate * 0.12)));
    }

    return {
      pcm: concatPcm(chunks),
      sampleRate,
      nativeSpeedApplied: true,
    };
  },

  cancel: () => {
    abortFlag = true;
  },

  unload: async () => {
    ttsInstance = null;
    loadPromise = null;
  },
};
